import React, { useState, useEffect, useRef } from 'react';
import { fetchGallerier, fetchGalleriBilleder, fetchDedicatedGallery, parseImageCaptions } from '../lib/cms';
import { imageUrl } from '../lib/images';
import AlbumModal from './AlbumModal';

export default function CarDecorationGallery() {
  const [galleries, setGalleries] = useState([]);
  const [dedicatedBilleder, setDedicatedBilleder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alle'); // 'alle', 'varebiler', 'privat'
  const [currentPage, setCurrentPage] = useState(1);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const galleryRef = useRef(null);

  const PAGE_SIZE = 12;

  useEffect(() => {
    async function loadCarGalleries() {
      setLoading(true);
      const [allGalleries, varebilerItems, privatItems] = await Promise.all([
        fetchGallerier(),
        fetchDedicatedGallery('galleri_dekoration_varebiler', 'dekoration-bil-varebiler'),
        fetchDedicatedGallery('galleri_dekoration_privat', 'dekoration-bil-privat')
      ]);
      setGalleries(allGalleries);
      let combined = [...(varebilerItems || []), ...(privatItems || [])];
      if (combined.length === 0) {
        combined = await fetchGalleriBilleder();
      }
      setDedicatedBilleder(combined || []);
      setLoading(false);
    }
    loadCarGalleries();
  }, []);

  // Find de specifikke gallerier fra CMS
  const varebilerGallery = galleries.find(
    (g) => g.side_slug === 'dekoration-bil-varebiler' || g.page_slug === 'dekoration-bil-varebiler'
  );

  const privatGallery = galleries.find(
    (g) => g.side_slug === 'dekoration-bil-privat' || g.page_slug === 'dekoration-bil-privat'
  );

  const defaultBilGallery = galleries.find(
    (g) => g.side_slug === 'dekoration-bil' || g.page_slug === 'dekoration-bil'
  );

  // Udtræk billeder med metadata og CMS-billedtekster (prioriterer den nye galleri_billeder samling)
  const varebilerImages = React.useMemo(() => {
    const dedicated = dedicatedBilleder
      .filter((b) => b.galleri_slug === 'dekoration-bil-varebiler')
      .sort((a, b) => (a.sortering || 99) - (b.sortering || 99));

    const dedicatedUrls = new Set(dedicated.map((b) => b.url));
    const extraFromGallery = [];
    if (varebilerGallery && varebilerGallery.images && varebilerGallery.images.length > 0) {
      const captions = parseImageCaptions(varebilerGallery.billedtekster, varebilerGallery.images.length);
      varebilerGallery.images.forEach((url, idx) => {
        if (!dedicatedUrls.has(url)) {
          const cap = captions[idx];
          extraFromGallery.push({
            url,
            id: `varebil-extra-${idx}`,
            category: 'varebiler',
            categoryLabel: 'Varebiler & Firmabiler',
            title: cap?.title || 'Firmabildekoration & Varebil',
            description: cap?.description || varebilerGallery.description || 'Professionel firmabildekoration, logoer og flådebranding'
          });
        }
      });
    }

    if (dedicated.length > 0 || extraFromGallery.length > 0) {
      const mappedDedicated = dedicated.map((b, idx) => {
        const imgs = (b.images && b.images.length > 0) ? b.images : (b.url ? [b.url] : []);
        return {
          url: imgs[0] || b.url,
          images: imgs,
          imageCount: imgs.length,
          isAlbum: imgs.length > 1,
          id: b.id || `varebil-${idx}`,
          category: 'varebiler',
          categoryLabel: 'Varebiler & Firmabiler',
          title: b.titel || 'Firmabildekoration & Varebil',
          description: b.beskrivelse || varebilerGallery?.description || 'Professionel firmabildekoration, logoer og flådebranding'
        };
      });
      return [...mappedDedicated, ...extraFromGallery];
    }

    return [];
  }, [dedicatedBilleder, varebilerGallery]);

  const privatImages = React.useMemo(() => {
    const dedicated = dedicatedBilleder
      .filter((b) => b.galleri_slug === 'dekoration-bil-privat')
      .sort((a, b) => (a.sortering || 99) - (b.sortering || 99));

    const dedicatedUrls = new Set(dedicated.map((b) => b.url));
    const extraFromGallery = [];
    if (privatGallery && privatGallery.images && privatGallery.images.length > 0) {
      const captions = parseImageCaptions(privatGallery.billedtekster, privatGallery.images.length);
      privatGallery.images.forEach((url, idx) => {
        if (!dedicatedUrls.has(url)) {
          const cap = captions[idx];
          extraFromGallery.push({
            url,
            id: `privat-extra-${idx}`,
            category: 'privat',
            categoryLabel: 'Private biler & Styling',
            title: cap?.title || 'Car Wrap & Styling',
            description: cap?.description || privatGallery.description || 'Eksklusiv helfarveskift, chrome delete og styling'
          });
        }
      });
    }

    if (dedicated.length > 0 || extraFromGallery.length > 0) {
      const mappedDedicated = dedicated.map((b, idx) => {
        const imgs = (b.images && b.images.length > 0) ? b.images : (b.url ? [b.url] : []);
        return {
          url: imgs[0] || b.url,
          images: imgs,
          imageCount: imgs.length,
          isAlbum: imgs.length > 1,
          id: b.id || `privat-${idx}`,
          category: 'privat',
          categoryLabel: 'Private biler & Styling',
          title: b.titel || 'Car Wrap & Styling',
          description: b.beskrivelse || privatGallery?.description || 'Eksklusiv helfarveskift, chrome delete og styling'
        };
      });
      return [...mappedDedicated, ...extraFromGallery];
    }

    return [];
  }, [dedicatedBilleder, privatGallery]);

  // Hvis subgallerier mangler, brug fallback fra det generelle bildekoration galleri
  const fallbackImages = React.useMemo(() => {
    if (varebilerImages.length === 0 && privatImages.length === 0 && defaultBilGallery?.images) {
      const captions = parseImageCaptions(defaultBilGallery.billedtekster, defaultBilGallery.images.length);
      return defaultBilGallery.images.map((url, idx) => {
        const cap = captions[idx];
        return {
          url,
          id: `bil-fallback-${idx}`,
          category: 'varebiler',
          categoryLabel: 'Bildekoration',
          title: cap?.title || 'Udført bildekoration',
          description: cap?.description || 'Professionel bildekoration udført af CRE8IT'
        };
      });
    }
    return [];
  }, [varebilerImages, privatImages, defaultBilGallery]);

  // Samlet liste over alle bilbilleder
  const allImages = React.useMemo(() => {
    if (varebilerImages.length > 0 || privatImages.length > 0) {
      return [...varebilerImages, ...privatImages];
    }
    return fallbackImages;
  }, [varebilerImages, privatImages, fallbackImages]);

  // Filtrerede billeder afhængig af aktiv fane
  const filteredImages = React.useMemo(() => {
    if (activeTab === 'varebiler') return varebilerImages.length > 0 ? varebilerImages : allImages;
    if (activeTab === 'privat') return privatImages.length > 0 ? privatImages : allImages;
    return allImages;
  }, [activeTab, varebilerImages, privatImages, allImages]);

  // Nulstil side ved skift af fane
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    if (galleryRef.current) {
      const rect = galleryRef.current.getBoundingClientRect();
      if (rect.top < 0) {
        galleryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Paginering
  const totalImages = filteredImages.length;
  const totalPages = Math.ceil(totalImages / PAGE_SIZE) || 1;
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const currentImages = filteredImages.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    if (galleryRef.current) {
      galleryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#325d52] border-t-transparent" />
      </div>
    );
  }

  // Coverbilleder til album-kortene
  const varebilCover =
    varebilerGallery?.cover_image?.[0] ||
    varebilerImages[0]?.url ||
    'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-1602_99c71551.jpeg';

  const privatCover =
    privatGallery?.cover_image?.[0] ||
    privatImages[0]?.url ||
    'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-9902.jpeg';

  const currentActiveImage = lightboxIndex !== null ? filteredImages[lightboxIndex] : null;

  return (
    <div ref={galleryRef} className="space-y-10 scroll-mt-24">
      {/* 1. Visuelle Kategori-/Album-kort i toppen for hurtig overskuelighed - Enkelt og stilrent design */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {/* Kort 1: Varebiler & Firmabiler */}
        <button
          type="button"
          onClick={() => handleTabChange('varebiler')}
          className={`group relative h-36 sm:h-44 w-full overflow-hidden rounded-2xl border-2 text-left transition-all duration-300 cursor-pointer shadow-xs hover:shadow-md ${
            activeTab === 'varebiler'
              ? 'border-[#325d52] ring-4 ring-[#325d52]/15'
              : 'border-gray-200 hover:border-[#325d52]/50'
          }`}
        >
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=700,quality=80,format=auto/${varebilCover.replace(
              'https://assets.auxodev.dk/',
              ''
            )}`}
            alt="Varebiler og firmabiler"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#14352f]/98 via-[#245b53]/95 to-[#325d52]/92 transition-all duration-300 group-hover:from-[#0e2722]/98 group-hover:via-[#1a453e]/96 group-hover:to-[#285047]/95" />

          <div className="relative h-full flex flex-col justify-end p-4 sm:p-5 text-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white group-hover:text-[#deecd4] transition-colors">
                  Varebiler & Firmabiler
                </h3>
                <p className="text-xs sm:text-sm text-gray-200 mt-0.5">
                  {varebilerImages.length} billeder
                </p>
              </div>
              <div
                className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center transition-all ${
                  activeTab === 'varebiler'
                    ? 'bg-[#deecd4] text-[#1b453f]'
                    : 'bg-white/20 backdrop-blur-xs text-white group-hover:bg-white group-hover:text-gray-900'
                }`}
              >
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>
          </div>
        </button>

        {/* Kort 2: Private biler & Styling */}
        <button
          type="button"
          onClick={() => handleTabChange('privat')}
          className={`group relative h-36 sm:h-44 w-full overflow-hidden rounded-2xl border-2 text-left transition-all duration-300 cursor-pointer shadow-xs hover:shadow-md ${
            activeTab === 'privat'
              ? 'border-[#325d52] ring-4 ring-[#325d52]/15'
              : 'border-gray-200 hover:border-[#325d52]/50'
          }`}
        >
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=700,quality=80,format=auto/${privatCover.replace(
              'https://assets.auxodev.dk/',
              ''
            )}`}
            alt="Private biler og car wrapping"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#14352f]/98 via-[#245b53]/95 to-[#325d52]/92 transition-all duration-300 group-hover:from-[#0e2722]/98 group-hover:via-[#1a453e]/96 group-hover:to-[#285047]/95" />

          <div className="relative h-full flex flex-col justify-end p-4 sm:p-5 text-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white group-hover:text-[#deecd4] transition-colors">
                  Private biler & Car Wrap
                </h3>
                <p className="text-xs sm:text-sm text-gray-200 mt-0.5">
                  {privatImages.length} billeder
                </p>
              </div>
              <div
                className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center transition-all ${
                  activeTab === 'privat'
                    ? 'bg-[#deecd4] text-[#1b453f]'
                    : 'bg-white/20 backdrop-blur-xs text-white group-hover:bg-white group-hover:text-gray-900'
                }`}
              >
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* 3. Billedgitter med afbalancerede kort */}
      {currentImages.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {currentImages.map((item, index) => {
            const globalIndex = startIndex + index;
            return (
              <div
                key={item.id || globalIndex}
                onClick={() => setLightboxIndex(globalIndex)}
                className="group relative cursor-pointer overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-xs transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="aspect-[4/3] w-full overflow-hidden bg-gray-100 relative">
                  <img
                    src={imageUrl(item.url, 'width=600,quality=80,format=auto')}
                    alt={item.title || 'Bildekoration opgave'}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                  />

                  {/* Badge hvis albummet har flere billeder */}
                  {item.imageCount > 1 && (
                    <div className="absolute top-2.5 right-2.5 z-10">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-black/75 px-2.5 py-0.5 text-xs font-semibold text-white backdrop-blur-md border border-white/20 shadow-md group-hover:bg-[#325d52] transition-colors">
                        <i className="fa-solid fa-layer-group text-[#deecd4] text-[10px]" />
                        <span>{item.imageCount} billeder</span>
                      </span>
                    </div>
                  )}

                  {/* Billedtekst i bunden */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-3 pt-6 text-white transition-opacity">
                    <p className="text-xs sm:text-sm font-bold truncate text-white drop-shadow-sm group-hover:text-[#deecd4] transition-colors">
                      {item.title}
                    </p>
                    {item.description && (
                      <p className="text-[11px] text-gray-200 line-clamp-1 opacity-90 mt-0.5">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Hover-overlay med forstørrelsesikon i toppen */}
                {(!item.imageCount || item.imageCount <= 1) && (
                  <div className="absolute top-2.5 right-2.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 z-10">
                    <div className="h-9 w-9 rounded-xl bg-black/60 backdrop-blur-xs text-white border border-white/20 flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-105">
                      <i className="fa-solid fa-magnifying-glass-plus text-xs text-[#deecd4]" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center text-gray-500">
          <i className="fa-solid fa-image text-3xl text-gray-300 mb-3" />
          <p className="text-sm font-medium">Ingen billeder fundet i denne kategori endnu.</p>
        </div>
      )}

      {/* 4. Paginering så siden forbliver hurtig og overskuelig */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 font-medium order-2 sm:order-1">
            Side {currentPage} af {totalPages} ({totalImages} billeder i alt)
          </p>

          <div className="flex items-center gap-2 order-1 sm:order-2">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold border border-gray-200 bg-white text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-xs"
            >
              <i className="fa-solid fa-chevron-left text-[10px]" />
              <span>Forrige</span>
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => handlePageChange(pageNum)}
                  className={`h-9 w-9 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    currentPage === pageNum
                      ? 'bg-[#325d52] text-white shadow-xs'
                      : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold border border-gray-200 bg-white text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-xs"
            >
              <span>Næste</span>
              <i className="fa-solid fa-chevron-right text-[10px]" />
            </button>
          </div>
        </div>
      )}

      {/* 5. Album Viewer Modal */}
      <AlbumModal
        isOpen={lightboxIndex !== null}
        album={currentActiveImage}
        onClose={() => setLightboxIndex(null)}
        onPrevAlbum={
          lightboxIndex !== null && lightboxIndex > 0
            ? () => setLightboxIndex(lightboxIndex - 1)
            : null
        }
        onNextAlbum={
          lightboxIndex !== null && lightboxIndex < filteredImages.length - 1
            ? () => setLightboxIndex(lightboxIndex + 1)
            : null
        }
        prevAlbumTitle={
          lightboxIndex !== null && lightboxIndex > 0
            ? filteredImages[lightboxIndex - 1]?.title
            : ''
        }
        nextAlbumTitle={
          lightboxIndex !== null && lightboxIndex < filteredImages.length - 1
            ? filteredImages[lightboxIndex + 1]?.title
            : ''
        }
      />
    </div>
  );
}
